(() => {
  const STORAGE_KEY = 'cropcrates_wishlist_v1';
  const TOAST_DURATION = 4200;

  function normalizeWishlist(items) {
    if (!Array.isArray(items)) return [];

    const normalized = [];
    const seen = new Set();

    items.forEach((item) => {
      let entry = null;

      if (typeof item === 'string' && item) {
        entry = { handle: item, variantId: null };
      } else if (item && typeof item === 'object' && item.handle) {
        entry = {
          handle: String(item.handle),
          variantId: item.variantId ? String(item.variantId) : null,
        };
      }

      if (!entry || seen.has(entry.handle)) return;

      seen.add(entry.handle);
      normalized.push(entry);
    });

    return normalized;
  }

  function getWishlist() {
    try {
      return normalizeWishlist(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch (error) {
      return [];
    }
  }

  function saveWishlist(items) {
    const normalized = normalizeWishlist(items);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      return;
    }

    document.dispatchEvent(new CustomEvent('cropcrates:wishlist-change'));
  }

  function getWishlistEntry(handle) {
    return getWishlist().find((item) => item.handle === handle) || null;
  }

  function getSelectedVariantId(button) {
    const productSection = button.closest('.product-information') || document;
    const variantInput = productSection.querySelector(
      'form[data-type="add-to-cart-form"] input[name="id"], product-form-component input[name="id"]'
    );

    return variantInput?.value || button.dataset.productVariantId || null;
  }

  function updateWishlistButtons() {
    const handles = new Set(getWishlist().map((item) => item.handle));

    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const handle = button.dataset.productHandle;
      const active = handles.has(handle);

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      const label = button.querySelector('[data-wishlist-label]');

      if (label) {
        label.textContent = active
          ? button.dataset.removeText || 'Added to Wishlist'
          : button.dataset.addText || 'Add to Wishlist';
      }
    });
  }

  function updateWishlistCount() {
    const count = getWishlist().length;

    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });

    document.querySelectorAll('.cropcrates-header-wishlist').forEach((link) => {
      link.setAttribute('aria-label', count > 0 ? `Wishlist (${count})` : 'Wishlist');
    });
  }

  function showWishlistToast(productTitle) {
    document.querySelector('[data-cropcrates-wishlist-toast]')?.remove();

    const toast = document.createElement('div');
    toast.className = 'cropcrates-wishlist-toast';
    toast.dataset.cropcratesWishlistToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const check = document.createElement('span');
    check.className = 'cropcrates-wishlist-toast__check';
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML = `
      <svg viewBox="0 0 24 24" width="18" height="18">
        <path d="m5 12.5 4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `;

    const message = document.createElement('div');
    message.className = 'cropcrates-wishlist-toast__message';

    const strong = document.createElement('strong');
    strong.textContent = productTitle || 'Product';
    message.appendChild(strong);
    message.appendChild(document.createTextNode(' has been added to your wishlist successfully.'));

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'cropcrates-wishlist-toast__close';
    close.setAttribute('aria-label', 'Close wishlist notification');
    close.textContent = '×';

    toast.appendChild(check);
    toast.appendChild(message);
    toast.appendChild(close);
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('is-visible'));

    const removeToast = () => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    };

    close.addEventListener('click', removeToast, { once: true });
    window.setTimeout(removeToast, TOAST_DURATION);
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function productImageUrl(url, width = 900) {
    if (!url) return '';

    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', width);
      return imageUrl.toString();
    } catch (error) {
      return url;
    }
  }

  function getCardVariant(product, entry) {
    const variants = Array.isArray(product.variants) ? product.variants : [];

    if (entry?.variantId) {
      const savedVariant = variants.find(
        (variant) => String(variant.id) === String(entry.variantId)
      );

      if (savedVariant) return savedVariant;
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function createWishlistCard(product, currency, entry) {
    const card = document.createElement('article');
    card.className = 'cropcrates-wishlist-card';

    const variant = getCardVariant(product, entry);

    const productLink = document.createElement('a');
    productLink.className = 'cropcrates-wishlist-card__image-link';
    productLink.href =
      product.url || `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;

    if (variant?.id) {
      try {
        const productUrl = new URL(productLink.href, window.location.origin);
        productUrl.searchParams.set('variant', String(variant.id));
        productLink.href = productUrl.toString();
      } catch (error) {
        // Keep the base product URL if URL parsing fails.
      }
    }

    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = productImageUrl(product.featured_image);
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.className = 'cropcrates-wishlist-card__image';
      productLink.appendChild(image);
    }

    const info = document.createElement('div');
    info.className = 'cropcrates-wishlist-card__info';

    const metaRow = document.createElement('div');
    metaRow.className = 'cropcrates-wishlist-card__meta-row';

    const titleLink = document.createElement('a');
    titleLink.className = 'cropcrates-wishlist-card__title';
    titleLink.href = productLink.href;
    titleLink.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'cropcrates-wishlist-card__price';

    const currentPrice = document.createElement('span');
    currentPrice.textContent = formatMoney(variant?.price ?? product.price, currency);
    price.appendChild(currentPrice);

    const compareAtPrice = variant?.compare_at_price ?? product.compare_at_price;
    const currentPriceValue = variant?.price ?? product.price;

    if (compareAtPrice && Number(compareAtPrice) > Number(currentPriceValue)) {
      const comparePrice = document.createElement('s');
      comparePrice.textContent = formatMoney(compareAtPrice, currency);
      price.appendChild(comparePrice);
    }

    metaRow.appendChild(titleLink);
    metaRow.appendChild(price);

    const addToCart = document.createElement('button');
    addToCart.type = 'button';
    addToCart.className = 'cropcrates-wishlist-card__add';
    addToCart.dataset.wishlistAddToCart = '';

    if (variant?.id && variant.available !== false) {
      addToCart.dataset.variantId = String(variant.id);
      addToCart.textContent = 'ADD TO BAG';
    } else {
      addToCart.disabled = true;
      addToCart.textContent = 'SOLD OUT';
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cropcrates-wishlist-card__remove';
    remove.dataset.wishlistRemove = '';
    remove.dataset.productHandle = product.handle;
    remove.setAttribute('aria-label', `Remove ${product.title} from wishlist`);
    remove.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true" width="15" height="15">
        <path d="M6.75 3.75h10.5v16.5L12 16.5l-5.25 3.75V3.75Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
      </svg>
      <span>Remove</span>
    `;

    info.appendChild(metaRow);
    info.appendChild(addToCart);
    info.appendChild(remove);

    card.appendChild(productLink);
    card.appendChild(info);

    return card;
  }

  async function addWishlistItemToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  const originalText = button.textContent;

  button.disabled = true;
  button.textContent = 'ADDING...';

  try {
    /*
     * Use Atelier / Horizon's native Shopify cart action instead
     * of manually posting to /cart/add.js.
     *
     * This updates:
     * - cart data
     * - cart count
     * - cart drawer contents
     * - empty/non-empty drawer state
     * without refreshing the page.
     */
    if (!window.Shopify?.actions?.updateCart) {
      throw new Error('Shopify cart actions are not available.');
    }

    const result = await window.Shopify.actions.updateCart(
      {
        lines: [
          {
            merchandiseId: String(variantId),
            quantity: 1,
          },
        ],
      },
      {
        event: {
          context: 'standard-action',
          detail: {
            source: 'cropcrates-wishlist',
            sourceId: 'wishlist-page',
          },
        },
      }
    );

    if (result?.userErrors?.length) {
      throw new Error(
        result.userErrors.map((error) => error.message).join(', ')
      );
    }

    button.textContent = 'ADDED TO BAG';

    /*
     * Explicitly open the cart after it has been updated.
     * Atelier may already auto-open it, but this guarantees it.
     */
    if (window.Shopify?.actions?.openCart) {
      await window.Shopify.actions.openCart();
    }

    window.setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 1600);

  } catch (error) {
    console.error(
      '[Cropcrates wishlist] Add to bag failed:',
      error
    );

    button.textContent = 'TRY AGAIN';
    button.disabled = false;

    window.setTimeout(() => {
      button.textContent = originalText;
    }, 1800);
  }
}

  async function renderWishlistPage() {
    const page = document.querySelector('[data-cropcrates-wishlist-page]');
    if (!page) return;

    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const loading = page.querySelector('[data-wishlist-loading]');
    const entries = getWishlist();
    const currency = page.dataset.currency || 'USD';

    if (!entries.length) {
      grid.innerHTML = '';
      grid.hidden = true;
      loading.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    loading.hidden = false;
    grid.hidden = true;

    const root = window.Shopify?.routes?.root || '/';

    const requests = entries.map(async (entry) => {
      const response = await fetch(`${root}products/${encodeURIComponent(entry.handle)}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Unable to load ${entry.handle}`);
      return { product: await response.json(), entry };
    });

    const results = await Promise.allSettled(requests);
    grid.innerHTML = '';

    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      grid.appendChild(
        createWishlistCard(result.value.product, currency, result.value.entry)
      );
    });

    loading.hidden = true;

    if (!grid.children.length) {
      grid.hidden = true;
      empty.hidden = false;
    } else {
      grid.hidden = false;
      empty.hidden = true;
    }
  }

  function initializeWishlist() {
    updateWishlistButtons();
    updateWishlistCount();
    renderWishlistPage();
  }

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-wishlist-toggle]');

    if (wishlistButton) {
      event.preventDefault();

      const handle = wishlistButton.dataset.productHandle;
      if (!handle) return;

      const wishlist = getWishlist();
      const existing = wishlist.find((item) => item.handle === handle);

      if (existing) {
        saveWishlist(wishlist.filter((item) => item.handle !== handle));
      } else {
        const variantId = getSelectedVariantId(wishlistButton);
        saveWishlist([{ handle, variantId }, ...wishlist]);
        showWishlistToast(wishlistButton.dataset.productTitle || 'Product');
      }

      return;
    }

    const addToCartButton = event.target.closest('[data-wishlist-add-to-cart]');

    if (addToCartButton) {
      event.preventDefault();
      addWishlistItemToCart(addToCartButton);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');

    if (removeButton) {
      event.preventDefault();
      const handle = removeButton.dataset.productHandle;
      saveWishlist(getWishlist().filter((item) => item.handle !== handle));
      return;
    }
  });

  document.addEventListener('cropcrates:wishlist-change', initializeWishlist);
  document.addEventListener('shopify:section:load', initializeWishlist);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWishlist);
  } else {
    initializeWishlist();
  }

  window.addEventListener('pageshow', initializeWishlist);
})();
